import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';
import R from 'ramda';
import { compose } from 'recompose';
import { Loader, Card } from 'semantic-ui-react';
import WideCampaignCard from '../../components/wide-campaign-card';

import CampaignDashboardHeader from '../../components/campaign-dashboard-header';
import TabActionBar, { ViewModes } from '../../components/tab-action-bar';
import CampaignCard from '../../components/campaign-card';
import DyzioEmptyState from '../../components/dyzio-empty-state';
import AddCampaignModal from '../../components/add-campaign-modal';


import hasRESTClient from '../../hocs/has-rest-client';
import hasUserInfo from '../../hocs/has-user-info';
import hasJSONAPIClient from '../../hocs/has-jsonapi-client';
import hasLocalStorageClient from '../../hocs/has-localstorage-client';
import hasViewModeManagement from '../../hocs/has-viewmode-management';

import { CAMPAIGN_OWNER } from '../../utils/static-configuration';

import { CURRENCY_GBP } from '../../utils/helpers/currencies';


const CAMPAIGN_VERSION_TWO = 2;
const CAMPAIGN_PER_PAGE = 100;
const CAMPAIGN_TYPE_WORKFLOW = 'WORKFLOW';
const CLIENT_BRIEF_URL = 'https://s3-eu-west-1.amazonaws.com/cf-templates-o6s432he4qgr-eu-west-1/2016202Jd5-dyzio-aws-stack.json';

const sortByOptions = [
  { key: 'assetsCountAsc', text: 'Assets count (ASC)', value: { field: 'trackedAssetsCount', order: 'ASC' } },
  { key: 'assetsCountDesc', text: 'Assets date (DESC)', value: { field: 'trackedAssetsCount', order: 'DESC' } },

  { key: 'createdDateAsc', text: 'Created date (ASC)', value: { field: 'campaignPermission.createdDate', order: 'ASC' } },
  { key: 'createdDateDesc', text: 'Created date (DESC)', value: { field: 'campaignPermission.createdDate', order: 'DESC' } },

  { key: 'nameAsc', text: 'Name (ASC)', value: { field: 'campaign.name', order: 'ASC' } },
  { key: 'nameDesc', text: 'Name (DESC)', value: { field: 'campaign.name', order: 'DESC' } },
];

const INITIAL_FORM_STATE_FIELDS = {
  name: null,
  budget: null,
  liveDate: null,
  hashtags: null,
  currency: CURRENCY_GBP,
  endDate: null,
  version: 2,
  hasChanged: false,
  savedOk: false,
  isValidBudget: true,
};

class CampaignsV2 extends React.Component {
  static contextTypes = {
    router: PropTypes.object,
  };

  constructor(props) {
    super(props);
    const savedViewMode = props.dyzioStorage.get(props.dyzioStorage.KEYS.CAMPAGINS_LIST_VIEW_MODE);
    this.state = {
      isLoading: true,
      isFailed: false,
      isLoaded: false,
      records: [],
      viewMode: props.viewModeManager.getViewMode(savedViewMode),
      addCampaignModalVisible: false,
      sort: {
        field: 'trackedAssetsCount',
        order: 'DESC',
      },
      filter: {
        version: CAMPAIGN_VERSION_TWO,
      },
      // new campaign fields
      ...INITIAL_FORM_STATE_FIELDS,
    };
  }

  requestData = () => {
    const { restClient, userInfo } = this.props;
    const { userId } = userInfo;
    const { sort, filter } = this.state;
    restClient({
      method: 'post',
      url: '/campaigns/query',
      baseURL: process.env.REACT_APP_CAMPAIGNS_GET_URL,
      data: {
        data: {
          userId,
          perPage: CAMPAIGN_PER_PAGE,
          search: {},
          sort,
          filter,
        },
      },
    }).then(response => {
      const { data: { data: { data } } } = response;
      this.setState({
        records: data,
        isLoaded: true,
        isFailed: false,
        isLoading: false,
      });
    }).catch(() => {
      this.setState({
        isLoaded: true,
        isFailed: true,
        isLoading: false,
      });
    });
  }

  componentDidMount = () => {
    this.requestData();
  }

  onSortByChanged = (event, data) => {
    const { value } = data;
    this.setState({
      sort: value,
    }, this.requestData);
  }

  onViewModeChanged = (event, data) => {
    const { name } = data;
    const { dyzioStorage, viewModeManager } = this.props;
    this.setState({
      viewMode: name,
    }, () => {
      viewModeManager.updateViewMode(dyzioStorage.KEYS.CAMPAGINS_LIST_VIEW_MODE, name);
    });
  }

  toggleAddNewCampaignModalState = () => {
    const { addCampaignModalVisible } = this.state;
    const updatedState = {
      addCampaignModalVisible: !addCampaignModalVisible,
    };
    if (addCampaignModalVisible) {
      _.assign(updatedState, INITIAL_FORM_STATE_FIELDS);
    }
    this.setState(updatedState);
  }

  onAddNewCampaignSubmit = () => {
    const {
      jsonAPIClient,
      userInfo: {
        organisationId,
        userId,
        email,
      },
    } = this.props;

    const {
      name,
      budget,
      liveDate,
      endDate,
      currency,
      hashtags,
    } = this.state;

    // validate budget being a valid number
    const isValidBudget = !_.isNaN(_.toNumber(budget));

    if (!isValidBudget) {
      this.setState({ isValidBudget });
      return;
    }

    const campaign = jsonAPIClient.create('campaigns');

    const fields = {
      name,
      budget,
      campaignType: CAMPAIGN_TYPE_WORKFLOW,
      memberCount: 1,
      version: CAMPAIGN_VERSION_TWO,
      clientBrief: CLIENT_BRIEF_URL,
      currency,
      hashtags,
      description: `Description generated at ${moment().toISOString()}`,
    };

    _.forEach(fields, (value, key) => {
      if (value) {
        campaign.set(key, value);
      }
    });

    if (liveDate) {
      campaign.set('liveDate', liveDate.toISOString());
    }

    if (endDate) {
      campaign.set('endDate', endDate.toISOString());
    }

    campaign.sync().then(response => {
      const campaignResource = _.clone(response);
      const campaignJSON = response.toJSONTree();
      // Create campaign permission
      const campaignPermission = jsonAPIClient.create('campaignPermission');
      campaignPermission.set('campaign', { type: 'campaigns', id: campaignJSON.id });
      campaignPermission.set('user', { type: 'users', id: userId });
      campaignPermission.set('organisation', { type: 'organisations', id: organisationId });
      campaignPermission.set('email', email);
      campaignPermission.set('role', CAMPAIGN_OWNER);
      campaignPermission.sync().then(campaignPermissionResponse => {
        const campaignPermissionResource = _.clone(campaignPermissionResponse);
        campaignResource.relationships('members').add(campaignPermissionResource);
        campaignResource.sync().then(resource => {
          const createdCampaign = resource.toJSON();
          this.setState({
            addCampaignModalVisible: false,
            isLoading: true,
            isFailed: false,
            isLoaded: false,
            ...INITIAL_FORM_STATE_FIELDS,
          }, () => {
            this.context.router.push(`/campaign/v2/${createdCampaign.id}/assets`);
          });
        });
      });
    });
  }

  onInputChange = (event, { name, value }) => {
    this.setState({
      [name]: value,
      hasChanged: true,
      savedOk: false,
    });
  }

  onLiveDateChange = date => {
    this.setState({
      liveDate: date,
      hasChanged: true,
      savedOk: false,
    });
  }

  onEndDateChange = date => {
    this.setState({
      endDate: date,
      hasChanged: true,
      savedOk: false,
    });
  }

  onCurrencyChange = (event, data) => {
    const { value } = data;
    this.setState({
      currency: value,
    });
  }

  render() {
    const actionButtonProps = {
      onClick: this.toggleAddNewCampaignModalState,
      content: 'Add a campaign',
    };

    const {
      isLoading,
      isLoaded,
      isFailed,
      records,
      viewMode,
      addCampaignModalVisible,
      name,
      budget,
      liveDate,
      endDate,
      currency,
      hashtags,
      hasChanged,
      isValidBudget,
      savedOk,
    } = this.state;

    const addCampaignFormProps = {
      name,
      budget,
      liveDate,
      endDate,
      currency,
      hashtags,
      hasChanged,
      savedOk,
      onLiveDateChange: this.onLiveDateChange,
      onInputChange: this.onInputChange,
      onEndDateChange: this.onEndDateChange,
      onCurrencyChange: this.onCurrencyChange,
    };

    const newCampaignNameProvided = _.isString(name) && _.size(name) > 0;
    const budgetProvided = _.isString(budget);
    const liveDateProvided = _.isObject(liveDate);

    const canSubmitForm = R.all(arg => arg === true, [
      newCampaignNameProvided,
      budgetProvided,
      liveDateProvided,
    ]);

    const isLargeCardsViewMode = viewMode === ViewModes.cardsLarge;
    const isSmallCardsViewMode = viewMode === ViewModes.cardsSmall;
    const isTableView = viewMode === ViewModes.table;
    const isCardsViewMode = isLargeCardsViewMode || isSmallCardsViewMode;
    const noCampaignPermissions = _.isEmpty(records);
    const isLoadSuccess = isLoaded && !isFailed && !noCampaignPermissions;
    const isLoadEmptyData = isLoaded && !isFailed && noCampaignPermissions;
    return (
      <div>
        <div className="page">
          <CampaignDashboardHeader
            name="My Campaigns"
          />
          <div className="page-content">
            <TabActionBar
              hasActionButton
              actionButtonProps={actionButtonProps}
              onViewModeChanged={this.onViewModeChanged}
              shouldDisableButtons={isLoading || noCampaignPermissions}
              isLargeCards={isLargeCardsViewMode}
              isSmallCards={isSmallCardsViewMode}
              isTableView={isTableView}
              sortByOptions={sortByOptions}
              onSortByChanged={this.onSortByChanged}
            />
            { isLoading && <Loader active content="Loading campaigns..." /> }
            {
              isLoadEmptyData &&
              <DyzioEmptyState
                buttonLabel="Add a campaign"
                onClick={this.toggleAddNewCampaignModalState}
                content={
                  <div>
                    <p>There are no campaigns yet.</p>
                    <p>Use the button below to create a new campaign.</p>
                  </div>
                }
              />
            }
            {
              isLoadSuccess && isCardsViewMode &&
              <Card.Group itemsPerRow={isLargeCardsViewMode ? 4 : 6}>
                { _.map(records, record => <CampaignCard {...record} />)}
              </Card.Group>
            }
            {
              isLoadSuccess && isTableView &&
              <Card.Group itemsPerRow={1}>
                { _.map(records, record => <WideCampaignCard {...record} />)}
              </Card.Group>
            }
            {
              addCampaignModalVisible &&
              <AddCampaignModal
                onClose={this.toggleAddNewCampaignModalState}
                addCampaignFormProps={addCampaignFormProps}
                onAddCampaignSubmit={this.onAddNewCampaignSubmit}
                shouldDisableActionButton={!canSubmitForm}
                isValidBudget={isValidBudget}
              />
            }
          </div>
        </div>
      </div>
    );
  }
}

CampaignsV2.propTypes = {
  dyzioStorage: PropTypes.object.isRequired,
  viewModeManager: PropTypes.object.isRequired,
};


export default compose(
  hasRESTClient,
  hasJSONAPIClient,
  hasUserInfo,
  hasLocalStorageClient,
  hasViewModeManagement,
)(CampaignsV2);



/** WEBPACK FOOTER **
 ** ./src/containers/campaigns-v2/index.js
 **/